import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Callout: a boxed line the user must not miss. Four kinds, each ported from the kit:
//   rule      a firm rule (.wp-rule): inset, hairline, accent glyph
//   watch-out a watch-out (.wp-watch): soft amber, glyph beside the text
//   caution   an open caution card (.wp-nudge.is-open): amber edge bar and border
//   stop      an open stop card (.wp-nudge--stop.is-open): red edge bar and border
// The overline defaults to the catalogue's word for the kind; pass title to replace it. Actions
// (Show me, Got it) go in footer. The body is content from the session and is rendered as given.
import { cva } from "class-variance-authority";
import { t } from "../i18n/t.js";
import { Glyph } from "./button.js";
import { cn } from "./cn.js";
export const calloutVariants = cva("relative min-w-0 font-sans text-13 leading-body", {
    variants: {
        variant: {
            rule: "rounded-8 border border-wp-line bg-wp-bg/58 text-wp-text",
            "watch-out": "flex items-start gap-2 rounded-8 bg-caution/12 text-wp-text",
            caution: [
                "overflow-hidden rounded-12 border border-caution/48 bg-wp-panel shadow-2 text-wp-text",
                "bg-linear-to-b from-caution/11 to-caution/6",
                "before:absolute before:inset-y-0 before:left-0 before:w-0.75 before:bg-caution",
            ],
            stop: [
                "overflow-hidden rounded-12 border border-stop/60 bg-wp-panel shadow-2 text-wp-text",
                "bg-linear-to-b from-stop/20 to-stop/12",
                "before:absolute before:inset-y-0 before:left-0 before:w-0.75 before:bg-stop",
            ],
        },
        compact: { false: "", true: "" },
    },
    compoundVariants: [
        { variant: "rule", compact: false, className: "px-3 py-2.5" },
        { variant: "rule", compact: true, className: "px-2.5 py-2 text-12" },
        { variant: "watch-out", compact: false, className: "px-2.5 py-2" },
        { variant: "watch-out", compact: true, className: "px-2 py-1.5 text-12" },
        { variant: ["caution", "stop"], compact: false, className: "pt-3 pr-3 pb-2.5 pl-4" },
        { variant: ["caution", "stop"], compact: true, className: "pt-2.5 pr-2.5 pb-2 pl-3.5" },
    ],
    defaultVariants: { variant: "rule", compact: false },
});
const DEFAULT_TITLE = {
    rule: "step.firmRule",
    "watch-out": "step.watchOut",
    caution: "nudge.caution",
    stop: "nudge.stop",
};
const HEAD = {
    rule: "text-wp-text-3 [&>[aria-hidden]]:text-wp-accent",
    "watch-out": "text-caution",
    caution: "text-caution",
    stop: "text-stop-soft",
};
export function Callout({ className, variant, compact, title, icon, footer, children, ...props }) {
    const kind = variant ?? "rule";
    const overline = (_jsx("span", { className: "text-11 font-semibold leading-tight tracking-[0.08em] whitespace-nowrap uppercase", children: title ?? t(DEFAULT_TITLE[kind]) }));
    const body = _jsx("div", { className: kind === "rule" ? "mt-1.5 text-wp-text-2" : "mt-1", children: children });
    const foot = footer ? (_jsx("div", { className: "mt-2.5 flex flex-wrap items-center gap-2", children: footer })) : null;
    return (_jsx("div", { "data-variant": kind, "data-compact": compact ?? false, className: cn(calloutVariants({ variant, compact }), className), ...props, children: kind === "watch-out" ? (_jsxs(_Fragment, { children: [icon ? _jsx(Glyph, { className: "mt-0.5 text-caution", children: icon }) : null, _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("div", { className: HEAD[kind], children: overline }), _jsx("div", { className: "mt-px", children: children }), foot] })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: cn("flex items-center gap-1.5", HEAD[kind]), children: [icon ? _jsx(Glyph, { children: icon }) : null, overline] }), body, foot] })) }));
}
//# sourceMappingURL=callout.js.map